/* eslint-disable quote-props */
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import dayjs from 'dayjs';
import { FragmentType, graphql, useFragment as getFragmentData } from '@frontend/gql/social';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartLegendContent,
  ChartLegend,
} from '@frontend/shadcn/components/ui/chart';
import { Period } from '@frontend/gql/social/graphql';

import { ChartTooltipContent } from '../../../components/chart';
import { formatDateByPrecision } from '../../utils';

const ViewsPerPersonChartFragment = graphql(`
  fragment ViewsPerPersonChart_MetaInsights on MetaInsights {
    interval {
      date
    }
    frequency
  }
`);

type ViewsPerPersonChartProps = {
  currentIntervalsIntervalInsights:
    | (FragmentType<typeof ViewsPerPersonChartFragment> | null | undefined)[]
    | null
    | undefined;
  currentIntervalsIntervalInsightsNotAspireSourced:
    | (FragmentType<typeof ViewsPerPersonChartFragment> | null | undefined)[]
    | null
    | undefined;
  showNonAttributedData?: boolean;
  precision: Period;
};

const ViewsPerPersonChart = ({
  currentIntervalsIntervalInsights,
  currentIntervalsIntervalInsightsNotAspireSourced,
  showNonAttributedData = false,
  precision,
}: ViewsPerPersonChartProps) => {
  // Transform data for the chart - match entries by date instead of index
  const data = React.useMemo(() => {
    // Process the fragment data inside useMemo to avoid dependency issues
    const currentIntervalData =
      currentIntervalsIntervalInsights?.map((interval) => getFragmentData(ViewsPerPersonChartFragment, interval)) || [];

    const currentIntervalNotAspireSourcedData =
      currentIntervalsIntervalInsightsNotAspireSourced?.map((interval) =>
        getFragmentData(ViewsPerPersonChartFragment, interval),
      ) || [];

    // Filter out intervals with invalid dates and create a map for non-attributed data
    const validCurrentIntervals = currentIntervalData.filter((interval) => interval?.interval?.date != null);

    const notAspireSourcedMap = new Map<string, number>();

    currentIntervalNotAspireSourcedData.forEach((interval) => {
      if (interval?.interval?.date != null) {
        // Ensure we use string representation for consistent key type
        const dateKey =
          interval.interval.date instanceof Date
            ? interval.interval.date.toISOString()
            : String(interval.interval.date);
        notAspireSourcedMap.set(dateKey, interval.frequency || 0);
      }
    });

    return validCurrentIntervals
      .map((interval) => {
        const dateValue = interval?.interval?.date;
        if (!dateValue) return null; // This shouldn't happen due to filtering, but satisfies TypeScript

        // Ensure consistent string representation for map lookup
        const dateStr = dateValue instanceof Date ? dateValue.toISOString() : String(dateValue);
        // Fix: Use dayjs to parse date as local date instead of UTC to prevent timezone offset
        const date = dateValue instanceof Date ? dateValue : dayjs(dateValue, 'YYYY-MM-DD').toDate();
        const matchingNotAspireSourced = notAspireSourcedMap.get(dateStr) || 0;

        const baseData = {
          month: formatDateByPrecision(date, precision),
          'Attributed to Aspire': interval?.frequency || 0,
        };

        if (showNonAttributedData) {
          return {
            ...baseData,
            'Not Attributed to Aspire': matchingNotAspireSourced,
          };
        }

        return baseData;
      })
      .filter((item) => item !== null);
  }, [
    currentIntervalsIntervalInsights,
    currentIntervalsIntervalInsightsNotAspireSourced,
    showNonAttributedData,
    precision,
  ]);

  const chartConfig: ChartConfig = {
    'Attributed to Aspire': {
      label: 'Attributed to Aspire',
      color: 'hsl(var(--refresh-orange-4))',
    },
    ...(showNonAttributedData && {
      'Not Attributed to Aspire': {
        label: 'Not Attributed to Aspire',
        color: 'hsl(var(--refresh-orange-3))',
      },
    }),
  };

  return (
    <ChartContainer className="h-[300px]" config={chartConfig}>
      <LineChart data={data}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="month" tickLine={false} tickMargin={10} axisLine={false} tickFormatter={(value) => value} />
        <YAxis tickLine={false} axisLine={false} tickFormatter={(value: number) => value.toFixed(1)} />
        <ChartTooltip content={<ChartTooltipContent valueFormatter={(item) => Number(item.value).toFixed(1)} />} />
        <ChartLegend content={<ChartLegendContent />} />
        <Line
          type="linear"
          dataKey="Attributed to Aspire"
          stroke="hsl(var(--refresh-orange-4))"
          strokeWidth={2}
          dot={false}
        />
        {showNonAttributedData && (
          <Line
            type="linear"
            dataKey="Not Attributed to Aspire"
            stroke="hsl(var(--refresh-orange-3))"
            strokeWidth={2}
            dot={false}
          />
        )}
      </LineChart>
    </ChartContainer>
  );
};

export default ViewsPerPersonChart;
